import React from "react";
import { Link, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { SEmbedLoadingState } from "./SEmbedLoadingState";
import { useSEmbed } from "./useSEmbed";
import type { SEmbedConfig } from "@norma-bi/bi-api";
import { useSFullAppSession } from "./SFullAppContext";
import { rememberHomePath } from "./fullAppPaths";
import { useT } from "@/i18n";

export function SFullAppPage() {
  const { embedSession, isSwitching, switchError, switchBusiness, canPostToken } =
    useSFullAppSession();
  const location = useLocation();
  const { t, dir, language } = useT();
  React.useEffect(() => {
    const search = new URLSearchParams(location.search);
    search.delete("logout");
    const query = search.toString();
    rememberHomePath(`${location.pathname}${query ? `?${query}` : ""}${location.hash}`);
  }, [location]);
  const lastConfig = React.useRef<SEmbedConfig | undefined>(undefined);
  if (embedSession.data?.config) lastConfig.current = embedSession.data.config;
  const { isReady, stalled, iframeProps } = useSEmbed({
    embedSession,
    configFallback: lastConfig.current,
    onBusinessSelect: switchBusiness,
    canPostToken,
  });
  const error =
    switchError ??
    (embedSession.isError ? embedSession.error : null) ??
    (stalled ? new Error(t("embed.loadFailed")) : null);
  const staleContext =
    error && "response" in error && (error.response as Response | undefined)?.status === 409;
  return (
    <div className="relative h-full w-full min-h-0 min-w-0 bg-background">
      {error ? (
        <div
          role="alert"
          dir={dir}
          lang={language}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-background p-8 text-center text-foreground"
        >
          <p>
            {staleContext
              ? t("embed.dataContextChanged")
              : (switchError?.message ?? t("embed.loadFailed"))}
          </p>
          {/* S's profile menu holds Sign out, so without S this is the only way to log out. */}
          <Button asChild variant="outline">
            <Link to={`${location.pathname}?logout`}>{t("signOut.title")}</Link>
          </Button>
        </div>
      ) : (
        (!isReady || embedSession.isFetching || isSwitching) && (
          <div className="absolute inset-0 z-10">
            <SEmbedLoadingState />
          </div>
        )
      )}
      {lastConfig.current && (
        <iframe {...iframeProps} title="Foodics AI" className="h-full w-full border-0" />
      )}
    </div>
  );
}
